// src/services/sliderService.js

import api from './api'; // Importa a instância centralizada do Axios

// Define as URLs base a partir da variável de ambiente REACT_APP_API_URL
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Verificação de segurança para garantir que a variável de ambiente esteja definida
if (!API_BASE_URL) {
  console.error('A variável de ambiente REACT_APP_API_URL não está definida');
}

// Define UPLOADS_BASE_URL removendo '/api' da API_BASE_URL
const UPLOADS_BASE_URL = API_BASE_URL ? API_BASE_URL.replace('/api', '') : '';

// Função para obter a URL completa de uma imagem.
export const getImageUrl = (imagemUrl) => {
  if (!imagemUrl) {
    console.warn('URL da imagem não fornecida');
    return '';
  }

  try {
    // Se já for uma URL completa, retorna ela mesma
    if (imagemUrl.startsWith('http')) {
      return imagemUrl;
    }

    // Remove o caminho absoluto do Windows se existir
    const cleanPath = imagemUrl.replace(/^C:\\zenkroll\\/, '');

    // Substitui backslashes por forward slashes
    const formattedPath = cleanPath.replace(/\\/g, '/');

    // Remove prefixos de pasta e força o uso de sliders
    const normalizedPath = formattedPath
      .replace(/^uploads\//, '')   // Remove 'uploads/' inicial se existir
      .replace(/^destaques\//, '') // Remove 'destaques/' se existir
      .replace(/^sliders\//, '');  // Remove 'sliders/' se existir

    // Constrói a URL completa sempre usando /uploads/sliders/
    const fullUrl = `${UPLOADS_BASE_URL}/uploads/sliders/${normalizedPath}`;

    console.log('Processamento da URL da imagem:', {
      original: imagemUrl,
      cleaned: cleanPath,
      formatted: formattedPath,
      normalized: normalizedPath,
      final: fullUrl,
      baseUrl: UPLOADS_BASE_URL
    });

    return fullUrl;
  } catch (error) {
    console.error('Erro ao processar URL da imagem:', error);
    return '';
  }
};

/**
 * Obtém todos os sliders associados a uma instituição específica.
 * @param {number|string} instituicaoId - O ID da instituição.
 * @param {string} modo - 'publico' ou 'privado'
 * @returns {Promise<Object[]>} Lista de sliders.
 */
export const getSliders = async (instituicaoId, modo = 'privado') => {
  try {
    const endpoint = modo === 'publico'
      ? `/sliders/instituicao/${instituicaoId}/publico`
      : `/sliders/instituicao/${instituicaoId}`;

    console.log('Buscando sliders na URL:', `${API_BASE_URL}${endpoint}`);
    const response = await api.get(endpoint);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar sliders:', error);
    throw error;
  }
};

/**
 * Cria um novo slider com upload de imagem.
 * @param {FormData} formData - Os dados do slider, incluindo o arquivo de imagem.
 * @returns {Promise<Object>} O slider criado.
 */
export const createSlider = async (formData) => {
  try {
    const response = await api.post('/sliders', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao criar slider:', error);
    throw error;
  }
};

/**
 * Atualiza um slider existente com possível upload de nova imagem.
 * @param {number|string} sliderId - O ID do slider a ser atualizado.
 * @param {FormData} formData - Os dados atualizados do slider, incluindo o arquivo de imagem se for atualizado.
 * @returns {Promise<Object>} O slider atualizado.
 */
export const updateSlider = async (sliderId, formData) => {
  try {
    const response = await api.put(`/sliders/${sliderId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar slider:', error);
    throw error;
  }
};

/**
 * Deleta um slider específico.
 * @param {number|string} sliderId - O ID do slider a ser deletado.
 * @returns {Promise<void>}
 */
export const deleteSlider = async (sliderId) => {
  try {
    await api.delete(`/sliders/${sliderId}`);
  } catch (error) {
    console.error('Erro ao deletar slider:', error);
    throw error;
  }
};

/**
 * Atualiza a ordem dos sliders
 * @param {Array} sliders - Array com os IDs e novas ordens dos sliders
 * @returns {Promise<Object>} Mensagem de confirmação
 */
export const updateSliderOrder = async (sliders) => {
  try {
    const response = await api.put('/sliders/order/update', { sliders });
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar ordem dos sliders:', error);
    throw error;
  }
};

/**
 * Obtém um slider específico por ID
 * @param {number|string} sliderId - ID do slider
 * @returns {Promise<Object>} Dados do slider
 */
export const getSliderById = async (sliderId) => {
  try {
    const response = await api.get(`/sliders/${sliderId}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar slider:', error);
    throw error;
  }
};

// Criar o objeto de serviço com todos os métodos
const sliderService = {
  getSliders,
  createSlider,
  updateSlider,
  deleteSlider,
  getImageUrl,
  updateSliderOrder,
  getSliderById
};

// Exportar o objeto de serviço
export default sliderService;
